/**
 * Market page "Market Fit Distribution" bands — same stops as {@link MarketFitGrid}.
 * Use for any [0, 100] percentage (darker = higher).
 */
export const MARKET_FIT_DISTRIBUTION_BANDS = [
  { range: '90-99', label: 'Excellent', color: '#0B0F1A', textColor: '#FFFFFF' },
  { range: '80-89', label: 'Strong', color: '#102838', textColor: '#FFFFFF' },
  { range: '70-79', label: 'Moderate', color: '#1C485C', textColor: '#FFFFFF' },
  { range: '60-69', label: 'Moderate', color: '#286D7F', textColor: '#FFFFFF' },
  { range: '50-59', label: 'Low', color: '#3D8C91', textColor: '#0A1A2F' },
  { range: '40-49', label: 'Low', color: '#56B0AF', textColor: '#0A1A2F' },
  { range: '30-39', label: 'Low', color: '#89CDD4', textColor: '#0A1A2F' },
  { range: '20-29', label: 'Low', color: '#C6F2F0', textColor: '#0A1A2F' },
  { range: '10-19', label: 'Low', color: '#D8FAF8', textColor: '#0A1A2F' },
  { range: '0-9', label: 'Low', color: '#ECFFFE', textColor: '#0A1A2F' },
];

/** @param {number} pct0to100 */
export function getMarketFitDistributionStyleForPercent(pct0to100) {
  const pct = Math.max(0, Math.min(100, Number(pct0to100) || 0));
  const decile = Math.min(9, Math.floor(pct / 10));
  const idx = 9 - decile;
  const band = MARKET_FIT_DISTRIBUTION_BANDS[idx] || MARKET_FIT_DISTRIBUTION_BANDS[9];
  return { backgroundColor: band.color, color: band.textColor };
}

function parseHexRgb(hex) {
  const s = String(hex || '').trim();
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(s);
  if (!m) return null;
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

/** WCAG relative luminance (sRGB). */
function relativeLuminance(r, g, b) {
  const lin = (c) => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  const R = lin(r);
  const G = lin(g);
  const B = lin(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

/**
 * Pick #0A1A2F or #FFFFFF for legible text on a solid hex background.
 * Use for small charts (treemap cells) where band.textColor can sit on misleading mid-tones.
 * @param {string} backgroundHex
 * @param {{ threshold?: number }} [opts] default threshold 0.42 favors dark text on saturated mid teals
 */
export function getContrastingTextOnHex(backgroundHex, opts = {}) {
  const threshold = opts.threshold ?? 0.42;
  const rgb = parseHexRgb(backgroundHex);
  if (!rgb) return '#0A1A2F';
  const L = relativeLuminance(rgb.r, rgb.g, rgb.b);
  return L > threshold ? '#0A1A2F' : '#FFFFFF';
}
